import React from 'react';
import { companyInfo, stats } from '../data/mock';
import { Button } from './ui/button';
import { ArrowRight, ChevronDown, Check } from 'lucide-react';

const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Generate gear teeth points for SVG
  const generateGearPath = (cx, cy, outerRadius, innerRadius, teeth) => {
    const points = [];
    const angleStep = (2 * Math.PI) / teeth;
    const toothDepth = outerRadius - innerRadius;
    
    for (let i = 0; i < teeth; i++) {
      const angle = i * angleStep;
      const nextAngle = angle + angleStep / 2;
      
      // Outer point (tooth tip)
      points.push({
        x: cx + outerRadius * Math.cos(angle),
        y: cy + outerRadius * Math.sin(angle)
      });
      // Tooth side going in
      points.push({
        x: cx + (outerRadius - toothDepth * 0.3) * Math.cos(angle + angleStep * 0.15),
        y: cy + (outerRadius - toothDepth * 0.3) * Math.sin(angle + angleStep * 0.15)
      });
      // Inner point (valley)
      points.push({
        x: cx + innerRadius * Math.cos(nextAngle),
        y: cy + innerRadius * Math.sin(nextAngle)
      });
      // Tooth side going out
      points.push({
        x: cx + (outerRadius - toothDepth * 0.3) * Math.cos(nextAngle + angleStep * 0.35),
        y: cy + (outerRadius - toothDepth * 0.3) * Math.sin(nextAngle + angleStep * 0.35)
      });
    }
    
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#f8fafc] via-[#f1f5f9] to-[#e8f4f8]">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-50" />
      
      {/* Elevator Shaft/Cable Pattern - Left Side Only */}
      <div className="absolute left-0 top-0 bottom-0 w-32 opacity-[0.08] pointer-events-none">
        {/* Main Shaft */}
        <div className="absolute left-8 top-0 bottom-0 w-20 border-l-2 border-r-2 border-[#1a4db3]">
          {/* Guide Rails */}
          <div className="absolute left-2 top-0 bottom-0 w-1.5 bg-[#1a4db3]" />
          <div className="absolute right-2 top-0 bottom-0 w-1.5 bg-[#1a4db3]" />
          
          {/* Horizontal Brackets */}
          {[...Array(15)].map((_, i) => (
            <div key={i} className="absolute left-0 right-0 h-1 bg-[#1a4db3]" style={{ top: `${i * 6.67}%` }} />
          ))}
          
          {/* Cross braces */}
          {[...Array(7)].map((_, i) => (
            <div key={`brace-${i}`} className="absolute left-0 right-0" style={{ top: `${i * 14.28}%` }}>
              <div className="w-full h-8 border-l border-r border-[#1a4db3]/50" 
                   style={{ 
                     background: 'linear-gradient(45deg, transparent 45%, #1a4db3 45%, #1a4db3 55%, transparent 55%)',
                     backgroundSize: '8px 8px'
                   }} 
              />
            </div>
          ))}
        </div>
        
        {/* Cables */}
        <div className="absolute left-4 top-0 bottom-0 w-[2px] bg-[#1a4db3]" />
        <div className="absolute left-6 top-0 bottom-0 w-[1px] bg-[#1a4db3]" />
        <div className="absolute left-[116px] top-0 bottom-0 w-[1px] bg-[#1a4db3]" />
        <div className="absolute left-[120px] top-0 bottom-0 w-[2px] bg-[#1a4db3]" />
      </div>

      {/* Proper Gear with Teeth - Bottom Left */}
      <div className="absolute bottom-16 left-8 opacity-[0.06] pointer-events-none">
        <svg width="180" height="180" viewBox="0 0 180 180" fill="none">
          {/* Outer gear with teeth */}
          <path 
            d={generateGearPath(90, 90, 85, 65, 16)} 
            fill="#1a4db3"
          />
          {/* Inner circle */}
          <circle cx="90" cy="90" r="50" fill="#f8fafc" />
          {/* Hub */}
          <circle cx="90" cy="90" r="30" fill="#1a4db3" />
          {/* Center hole */}
          <circle cx="90" cy="90" r="12" fill="#f8fafc" />
          {/* Spokes */}
          {[...Array(6)].map((_, i) => (
            <line
              key={i}
              x1="90"
              y1="42"
              x2="90"
              y2="58"
              stroke="#f8fafc"
              strokeWidth="4"
              transform={`rotate(${i * 60} 90 90)`}
            />
          ))}
        </svg>
      </div>

      {/* Small secondary gear - meshing with main gear */}
      <div className="absolute bottom-40 left-36 opacity-[0.04] pointer-events-none">
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
          <path 
            d={generateGearPath(40, 40, 35, 25, 10)} 
            fill="#1a4db3"
          />
          <circle cx="40" cy="40" r="18" fill="#f8fafc" />
          <circle cx="40" cy="40" r="10" fill="#1a4db3" />
          <circle cx="40" cy="40" r="4" fill="#f8fafc" />
        </svg>
      </div>
      
      {/* Decorative Gradient Orbs */}
      <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-gradient-to-br from-[#1a4db3]/10 to-[#1f87ff]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-40 w-[400px] h-[400px] bg-gradient-to-br from-[#1f87ff]/10 to-[#1a4db3]/5 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-[#f5b041]/5 to-transparent rounded-full blur-3xl" />

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 ml-8 lg:ml-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            {/* Badge */}
            <div className="hero-animate inline-flex items-center gap-2 px-4 py-2 bg-[#1a4db3]/10 rounded-full border border-[#1a4db3]/20">
              <span className="text-sm font-semibold text-[#1a4db3] tracking-wide">35+ YEARS OF EXCELLENCE</span>
            </div>

            {/* Headline */}
            <div className="space-y-6 hero-animate-delay-1">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1e2430] leading-tight">
                Complete Elevator
                <span className="block text-[#1e2430]">Kits & Components</span>
                <span className="block gradient-text">Since 1989</span>
              </h1>
              <p className="text-lg text-[#4a5568] max-w-xl leading-relaxed">
                Shree Silos Group of Companies - Your one-stop solution for elevator kits and components. Manufacturing excellence with {companyInfo.experience} years of technical expertise.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 hero-animate-delay-2">
              <Button
                onClick={() => scrollToSection('#contact')}
                className="bg-[#1a4db3] hover:bg-[#1f87ff] text-white font-semibold px-8 py-6 text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/25 hover:-translate-y-1"
              >
                Request a Consultation
              </Button>
              <Button
                onClick={() => scrollToSection('#services')}
                variant="outline"
                className="border-[#d9e2ec] text-[#4a5568] hover:bg-[#f1f5f9] hover:border-[#1a4db3] hover:text-[#1a4db3] px-8 py-6 text-base rounded-xl transition-all duration-300 hover:-translate-y-1"
              >
                Explore Services
              </Button>
            </div>
          </div>

          {/* Glass Card with Stats */}
          <div className="hidden lg:block hero-animate-delay-2">
            <div className="glass-card-strong rounded-3xl p-8 max-w-md ml-auto">
              <div className="text-5xl font-bold text-[#1a4db3] mb-2">
                20,000<span className="text-xl font-normal text-[#4a5568] ml-2">sq.ft</span>
              </div>
              <p className="text-[#4a5568] mb-6">
                Precision manufacturing facility in Bangalore with 50+ skilled experts.
              </p>
              
              <div className="space-y-4">
                {[
                  'Nationwide delivery & installation',
                  'Advanced automation & quality control',
                  'Dedicated modernization expertise'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#1a4db3]" />
                    <span className="text-[#4a5568] text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <button
          onClick={() => scrollToSection('#about')}
          aria-label="Scroll to About section"
          className="flex flex-col items-center gap-2 text-[#4a5568] hover:text-[#1a4db3] transition-colors"
        >
          <ChevronDown className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
